import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import _ from "lodash";
import UsageTracking from "../tracking";
import { EventName } from "../tracking/tracking.types";
import { ButtonEvent, ComponentEvent, PageNameEvent, PageLoadEvent, OnboardingCompletedEvent } from "./eventTracker.types";

interface UseEventTrackerOptions {
  trackTabLaunch?: boolean;
  customTabLaunchCondition?: (isInitialized: boolean) => boolean;
}

const useEventTracker = (options: UseEventTrackerOptions = {}) => {
  const { trackTabLaunch = false, customTabLaunchCondition } = options;

  const { pathname } = useLocation();
  const segments = pathname.split("/");
  const tabName = segments[1] || "home";
  const pageName = segments[2] ?? null;

  // UsageTracking state management
  const [isUsageTrackingInitialized, setIsUsageTrackingInitialized] = useState(
    UsageTracking.Instance.isInitialized
  );
  const hasTrackedTabLaunch = useRef(false);

  // Subscribe to UsageTracking initialization
  useEffect(() => {
    const unsubscribe = UsageTracking.Instance.subscribeInitialized(
      setIsUsageTrackingInitialized
    );
    return unsubscribe;
  }, []);

  // Auto track tab launch when conditions are met
  useEffect(() => {
    if (trackTabLaunch && !hasTrackedTabLaunch.current) {
      const shouldTrack = customTabLaunchCondition
        ? customTabLaunchCondition(isUsageTrackingInitialized)
        : isUsageTrackingInitialized;

      if (shouldTrack) {
        // eventTrackOnTabLaunched();
        hasTrackedTabLaunch.current = true;
      }
    }
  }, [isUsageTrackingInitialized, trackTabLaunch, customTabLaunchCondition]);

  const eventTrackOnLogin = () => {
    return UsageTracking.Instance.setEventName(EventName.LOGIN).eventTracker();
  };

  const eventTrackOnAppLaunched = (event: PageNameEvent) => {
    return UsageTracking.Instance.setEventName(EventName.APP_LAUNCHED)
      .setNonPiiData({
        ...(event.tabName && { "tab-name": event.tabName }),
        ...(event.pageName && { "page-name": event.pageName }),
        ...(event.pendingDomains && {
          "pending-domains": event.pendingDomains,
        }),
        ...(event.approvedDomains && {
          "approved-domains": event.approvedDomains,
        }),
        ...(event.suspendedDomains && {
          "suspended-domains": event.suspendedDomains,
        }),
        ...(event.managedByAdm && {
          "managed-by-adm": event.managedByAdm,
        }),
        ...(event.managedByTac && {
          "managed-by-tac": event.managedByTac,
        }),
        ...(event.rank && {
          "rank": event.rank,
        }),
        ...(event.allowedDomains && {
          "allowed-domains": event.allowedDomains,
        }),
        ...(event.blockedDomains && {
          "blocked-domains": event.blockedDomains,
        }),
        ...(event.federationSettings && {
          "federation-settings": event.federationSettings,
        }),
      })
      .eventTracker();
  };


   const eventTrackOnComponentLoaded = (event: ComponentEvent) => {
    return UsageTracking.Instance.setEventName(EventName.COMPONENT_LOADED)
      .setNonPiiData({
        "component-type": event.componentType ?? "dialog",
        ...(event.pageName && { "page-name": event.pageName }),
        ...(event.componentName && { "component-name": event.componentName }),
        ...(event.ranks && { "ranks": event.ranks }),
      })
      .eventTracker();
  };

  const eventTrackOnPageLoaded = (event: PageLoadEvent) => {
    return UsageTracking.Instance.setEventName(EventName.PAGE_LOADED)
      .setNonPiiData({
        "page-name": event.pageName,
        "load-time-ms": event.loadTimeMs,
      })
      .eventTracker();
  };

  const eventTrackOnOnboardingCompleted = (event: OnboardingCompletedEvent) => {
    return UsageTracking.Instance.setEventName(EventName.ONBOARDING_COMPLETED)
      .setNonPiiData({
        "success": event.success,
        ...(event.errorMessage && { "error-message": event.errorMessage }),
      })
      .eventTracker();
  };


  const eventTrackOnClicked = (event: ButtonEvent) => {
    return UsageTracking.Instance.setEventName(EventName.BUTTON_CLICKED)
      .setNonPiiData(
        _.pickBy(
          {
            ...(event.pageName && { "page-name": event.pageName }),
            "button-name": event.buttonName,
          },
          _.identity
        )
      )
      .eventTracker();
  };

  const eventTrackOnElementClicked = (event: ButtonEvent) => {
    return UsageTracking.Instance.setEventName(EventName.ELEMENT_CLICKED)
      .setNonPiiData(
        _.pickBy(
          {
            ...(event.pageName && { "page-name": event.pageName }),
            ...(event.domains && { "domains": event.domains }),
            ...(event.assignmentType && { "based-policy": event.assignmentType }),
            ...(event.assignments && { "based-policy": event.assignments }),
            ...(event.domains && { "based-policy": event.domains }),
            ...(event.basedPolicyId && { "based-policy-id": event.basedPolicyId }),
            ...(event.assignmentType && { "assignment-type": event.assignmentType }),
            ...(event.assignments && { "assignments": event.assignments }),
            ...(event.elementName && { "element-name": event.elementName}),
            "element-type": event.elementType ?? "button",
            ...(event.ranks && { "ranks": event.ranks}),
            ...(event.policyName && { "policy-name": event.policyName }),
            ...(event.bilateralChat && { "bilateral-chat": event.bilateralChat }),
            ...(event.emailNotificationEnabled && { "email-notification-enabled": event.emailNotificationEnabled }),
            ...(event.fromAddress && { "from-address": event.fromAddress }),
            ...(event.defaultRecipients && { "default-recipients": event.defaultRecipients }),
            ...(event.emailForPolicyUpdates && { "email-for-policy-updates": event.emailForPolicyUpdates }),
            ...(event.emailForPolicyAlertsAndErrors && { "email-for-policy-alerts-and-errors": event.emailForPolicyAlertsAndErrors }),
            ...(event.automaticDomainFeedSync && { "automatic-domain-feed-sync": event.automaticDomainFeedSync }),
            ...(event.autoApproveNewDomains && { "auto-approve-new-domains": event.autoApproveNewDomains }),
            ...(event.restrictedDomains && { "restricted-domains": event.restrictedDomains }),
          },
          _.identity
        )
      )
      .eventTracker();
  };

  return {
    isUsageTrackingInitialized,
    hasTrackedTabLaunch: hasTrackedTabLaunch.current,
    eventTrackOnLogin,
    eventTrackOnAppLaunched,
    eventTrackOnComponentLoaded,
    eventTrackOnPageLoaded,
    eventTrackOnOnboardingCompleted,
    eventTrackOnClicked,
    eventTrackOnElementClicked,
  };
};
export default useEventTracker;
